/** Unresolved reaction reports (SUP-004). Listed until a staff member marks them resolved. */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { AdminReactionReport, AdminReactionReportList } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { api } from "./api.ts";
import { Person } from "./bits.tsx";
import { publishNotice } from "./notice.ts";
import type { Column } from "./layout.tsx";
import { PageHead, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href } from "./router.ts";
import { Button, ErrorText, Status } from "./ui.tsx";
import { moment, refusal } from "./words.ts";
const SEVERITY = {
  mild: "Mild",
  moderate: "Moderate",
  severe: "Severe"
} as const;
export function Reactions() {
  const [found, setFound] = useLoad("reaction-reports", () => api<AdminReactionReportList>(ROUTES.adminReactionReports));
  const [saving, setSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  async function resolve(id: string) {
    setSaving(id);
    setRowError(null);
    publishNotice("saving", "Saving");
    const result = await api<AdminReactionReportList>(ROUTES.adminResolveReaction, {
      params: {
        reportId: id
      }
    });
    setSaving(null);
    if (result.ok) {
      publishNotice("saved", "Saved");
      setFound(result.data);
      return;
    }
    const message = refusal(result.error);
    setRowError(message);
    publishNotice("failed", message);
  }
  const columns: readonly Column<AdminReactionReport>[] = [{
    label: "Patient",
    cell: row => <Person name={row.patientName ?? "Name not given"} to={href.patient(row.patientId)} />
  }, {
    label: "What they wrote",
    wide: true,
    cell: row => <p className="x1ghz6dp x6amaq2 x1mzt3pk">{row.description}</p>
  }, {
    label: "Severity",
    nowrap: true,
    phoneLabel: true,
    cell: row => row.severity === null ? <span className="x1jty25b">Not given</span> : <Status tone={row.severity === "severe" ? "danger" : "warn"}>
            {SEVERITY[row.severity]}
          </Status>
  }, {
    label: "Assigned",
    phoneLabel: true,
    cell: row => row.assignedEmail === null ? <span className="x1jty25b">Unassigned</span> : <Person name={row.assignedEmail} email={row.assignedEmail} />
  }, {
    label: "Reported",
    numeric: true,
    phoneLabel: true,
    cell: row => moment(row.createdAt)
  }, {
    label: "Resolve",
    nowrap: true,
    cell: row => <Button tone="quiet" disabled={saving === row.id} onClick={() => void resolve(row.id)}>
          {saving === row.id ? "Saving" : "Mark resolved"}
        </Button>
  }];
  if (found.state === "loading") return <PageHead title="Reaction reports" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <>
      <PageHead title="Reaction reports" meta={found.data.reports.length === 0 ? "None open" : `${found.data.reports.length} open`} />
      <div className="x78zum5 xdt5ytf xd3upel">
        <Section title="Open reports" count={found.data.reports.length}>
          <Table label="Open reaction reports" columns={columns} rows={found.data.reports} rowKey={row => row.id} empty="No open reaction reports." />
          {rowError === null ? null : <ErrorText>{rowError}</ErrorText>}
        </Section>
      </div>
    </>;
}